/**
 * Footer Block for Editor.js
 * Creates email-safe footer with EJ's branding and unsubscribe links
 */

import { BlockTool } from '@editorjs/editorjs';

export interface FooterBlockData {
  showHours: boolean;
  showAddress: boolean;
}

export default class FooterBlock implements BlockTool {
  private data: FooterBlockData;
  private wrapper: HTMLElement | null = null;

  static get toolbox() {
    return {
      title: "EJ's Footer",
      icon: '<svg width="17" height="15" viewBox="0 0 17 15" xmlns="http://www.w3.org/2000/svg"><rect x="1" y="10" width="15" height="4" rx="1" stroke="currentColor" stroke-width="1.5" fill="none"/><path d="M4 12h9" stroke="currentColor" stroke-width="1" stroke-linecap="round"/></svg>',
    };
  }

  constructor({ data }: { data: FooterBlockData }) {
    this.data = {
      showHours: data.showHours !== false,
      showAddress: data.showAddress !== false,
    };
  }

  render(): HTMLElement {
    this.wrapper = document.createElement('div');
    this.wrapper.classList.add('email-footer-block');
    this.updateWrapper();
    return this.wrapper;
  }

  private updateWrapper(): void {
    if (!this.wrapper) return;

    this.wrapper.style.cssText = `
      background-color: #f9f9f9;
      padding: 32px 40px;
      text-align: center;
      margin-top: 24px;
      border-radius: 4px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    `;

    let html = `
      <p style="margin: 0 0 16px; font-size: 16px; font-weight: 600; color: #2b2b2b;">
        EJ's Wine Shop & Tasting Room
      </p>
    `;

    if (this.data.showAddress) {
      html += `
        <p style="margin: 0 0 16px; font-size: 14px; line-height: 24px; color: #666666;">
          955 Washington St, Dubuque, IA 52001
        </p>
      `;
    }

    if (this.data.showHours) {
      html += `
        <p style="margin: 0 0 16px; font-size: 14px; line-height: 24px; color: #666666;">
          <strong>Hours:</strong><br>
          Tue-Thu: 3-10pm • Fri: 3-11pm • Sat: 2-11pm • Sun: 2-6pm • Mon: Closed
        </p>
      `;
    }

    html += `
      <p style="margin: 0 0 16px; font-size: 14px; color: #666666;">
        <a href="https://www.ejswineshop.com/contact" style="color: #D4A657; text-decoration: none; font-weight: 500;">Contact Us</a>
        <span style="color: #666666;"> | </span>
        <a href="https://www.ejswineshop.com/email-preferences?email={{email}}" style="color: #D4A657; text-decoration: none; font-weight: 500;">Email Preferences</a>
      </p>
      <p style="margin: 16px 0 0; font-size: 12px; line-height: 18px; color: #999999;">
        You're receiving this email because you're a valued member of EJ's Wine Shop.<br>
        <a href="https://www.ejswineshop.com/email-preferences?email={{email}}" style="color: #999999; text-decoration: underline; font-size: 12px;">Unsubscribe from this list</a>
      </p>
      <p style="margin: 16px 0 0; font-size: 12px; color: #999999;">
        © ${new Date().getFullYear()} EJ's Wine Shop. All rights reserved.
      </p>
    `;

    this.wrapper.innerHTML = html;
  }

  save(): FooterBlockData {
    return this.data;
  }

  renderSettings(): HTMLElement {
    const wrapper = document.createElement('div');
    wrapper.style.padding = '8px';

    // Show address toggle
    const addressLabel = document.createElement('label');
    addressLabel.style.cssText = 'display: flex; align-items: center; gap: 8px; margin-bottom: 12px; cursor: pointer;';

    const addressCheckbox = document.createElement('input');
    addressCheckbox.type = 'checkbox';
    addressCheckbox.checked = this.data.showAddress;
    addressCheckbox.addEventListener('change', () => {
      this.data.showAddress = addressCheckbox.checked;
      this.updateWrapper();
    });

    addressLabel.appendChild(addressCheckbox);
    addressLabel.appendChild(document.createTextNode('Show Address'));

    // Show hours toggle
    const hoursLabel = document.createElement('label');
    hoursLabel.style.cssText = 'display: flex; align-items: center; gap: 8px; cursor: pointer;';

    const hoursCheckbox = document.createElement('input');
    hoursCheckbox.type = 'checkbox';
    hoursCheckbox.checked = this.data.showHours;
    hoursCheckbox.addEventListener('change', () => {
      this.data.showHours = hoursCheckbox.checked;
      this.updateWrapper();
    });

    hoursLabel.appendChild(hoursCheckbox);
    hoursLabel.appendChild(document.createTextNode('Show Hours'));

    wrapper.appendChild(addressLabel);
    wrapper.appendChild(hoursLabel);

    return wrapper;
  }
}
